import React, { useState, useEffect, useMemo } from "react";
import {
  Form,
  Input,
  DatePicker,
  InputNumber,
  Button,
  Card,
  message,
  Alert,
} from "antd";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import useGlobalFilters from "../../hooks/useGlobalFilters";
import { createLetterProcessing } from "../Accounting/api/accounting";

const LetterProcessing = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const profile = useSelector((state) => state.profile);
  const { selectedBrands, hasSelectedBrands } = useGlobalFilters();

  // Get the first selected brand for form submission
  const selectedBrand =
    selectedBrands && selectedBrands.length > 0 ? selectedBrands[0] : null;

  const onFinish = async (values) => {
    if (!selectedBrand) {
      message.error("Please select a brand first");
      return;
    }

    setLoading(true);
    try {
      // Add brand ID to the form data
      const formData = {
        ...values,
        brand: selectedBrand.id,
      };

      // Format date to YYYY-MM-DD
      if (formData.date) {
        formData.date = formData.date.format("YYYY-MM-DD");
      }

      // Convert any empty strings to null
      Object.keys(formData).forEach((key) => {
        if (formData[key] === "") {
          formData[key] = null;
        }
      });

      // Create new record
      await createLetterProcessing(formData);
      message.success("Letter processing record created successfully");

      form.resetFields();
    } catch (error) {
      console.error(
        "Error saving letter processing record:",
        error.response || error
      );
      message.error("Failed to save letter processing record");
    } finally {
      setLoading(false);
    }
  };

  // Check if user has permission to access this page
  // Allow admin, super_admin, senior_accountant, and management roles
  // Also check page permissions if available
  const hasPermission = useMemo(() => {
    if (!profile) return false;

    const role = profile.role?.toLowerCase();
    // Allow these roles
    if (
      role === "admin" ||
      role === "super_admin" ||
      role === "senior_accountant" ||
      role === "management"
    ) {
      return true;
    }

    // Check page permissions if available
    if (profile.page_permissions) {
      const route = "/letter-processing";
      const permissions = profile.page_permissions[route] || [];
      return permissions.includes("view");
    }

    // Fallback: check old permissions array
    if (profile.permissions?.includes("letter_processing")) {
      return true;
    }

    return false;
  }, [profile]);

  if (!hasPermission) {
    return (
      <div className="container-fluid">
        <Alert
          message="Access Denied"
          description="You don't have permission to access Letter Processing."
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {!hasSelectedBrands && (
        <Alert
          message="Please select a brand"
          description="You need to select a brand to add letter processing records."
          type="warning"
          showIcon
          className="mb-4"
        />
      )}

      <h2 className="mb-4">Letter Processing</h2>

      {/* Form for adding records */}
      <Card title="Add Letter Processing Record" className="mb-4">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            letters_received: 0,
            letters_processed: 0,
            check_orders: 0,
            check_orders_value: 0,
            money_orders: 0,
            money_orders_value: 0,
            cc_orders: 0,
            cc_orders_value: 0,
            cc_declined: 0,
            cash_orders: 0,
            cash_orders_value: 0,
            dnm: 0,
            misc: 0,
          }}
        >
          <div className="row">
            <div className="col-md-4">
              <Form.Item
                name="date"
                label="Date"
                rules={[{ required: true, message: "Please select a date" }]}
              >
                <DatePicker className="w-100" format="YYYY-MM-DD" />
              </Form.Item>
            </div>
            <div className="col-md-4">
              <Form.Item
                name="source"
                label="Source"
                rules={[{ required: true, message: "Please enter the source" }]}
              >
                <Input />
              </Form.Item>
            </div>
            <div className="col-md-4">
              <Form.Item name="tracking_number" label="Tracking Number">
                <Input />
              </Form.Item>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <Form.Item
                name="letters_received"
                label="Letters Received"
                rules={[
                  {
                    required: true,
                    message: "Please enter the number of letters received",
                  },
                ]}
              >
                <InputNumber min={0} className="w-100" />
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item
                name="letters_processed"
                label="Letters Processed"
                rules={[
                  {
                    required: true,
                    message: "Please enter the number of letters processed",
                  },
                ]}
              >
                <InputNumber min={0} className="w-100" />
              </Form.Item>
            </div>
          </div>

          <h5 className="mt-3">Order Details</h5>
          <div className="row">
            <div className="col-md-3">
              <Form.Item
                name="check_orders"
                label="Check Orders"
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber min={0} className="w-100" />
              </Form.Item>
            </div>
            <div className="col-md-3">
              <Form.Item
                name="check_orders_value"
                label="Value of Check Orders"
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  precision={2}
                  className="w-100"
                />
              </Form.Item>
            </div>
            <div className="col-md-3">
              <Form.Item
                name="money_orders"
                label="Money Orders"
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber min={0} className="w-100" />
              </Form.Item>
            </div>
            <div className="col-md-3">
              <Form.Item
                name="money_orders_value"
                label="Value of Money Orders"
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  precision={2}
                  className="w-100"
                />
              </Form.Item>
            </div>
          </div>

          <div className="row">
            <div className="col-md-3">
              <Form.Item
                name="cc_orders"
                label="CC Orders"
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber min={0} className="w-100" />
              </Form.Item>
            </div>
            <div className="col-md-3">
              <Form.Item
                name="cc_orders_value"
                label="Value of CC Orders"
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  precision={2}
                  className="w-100"
                />
              </Form.Item>
            </div>
            <div className="col-md-3">
              <Form.Item
                name="cc_declined"
                label="CC's Declined"
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber min={0} className="w-100" />
              </Form.Item>
            </div>
            <div className="col-md-3">
              <Form.Item
                name="dnm"
                label="DNM (Do Not Mail)"
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber min={0} className="w-100" />
              </Form.Item>
            </div>
          </div>

          <div className="row">
            <div className="col-md-3">
              <Form.Item
                name="cash_orders"
                label="Cash Orders"
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber min={0} className="w-100" />
              </Form.Item>
            </div>
            <div className="col-md-3">
              <Form.Item
                name="cash_orders_value"
                label="Value of Cash Orders"
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  precision={2}
                  className="w-100"
                />
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item
                name="misc"
                label="Miscellaneous"
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  precision={2}
                  className="w-100"
                />
              </Form.Item>
            </div>
          </div>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={!selectedBrand}
            >
              Submit
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default LetterProcessing;
